/**
 * HomeProsBlogs.js
 * ------------------------------------------------------------
 * Single-file blog data + React components (MUI) for:
 * - Cleaning Services
 * - HVAC Services
 * - Water Mitigation Services
 * - Roofing Services
 *
 * Project: Home Pros (Bloomington, Indiana)
 *
 * Usage:
 *   import AllBlogs, { BLOG_CATEGORIES, BLOGS } from "./HomeProsBlogs";
 *   // Render <AllBlogs /> anywhere in your app.
 *
 * Notes:
 * - No router required. Clicking a blog card opens details view inline.
 * - Blog content is structured as blocks so line breaks, highlights,
 *   lists, and callouts render nicely when mapped.
 */

import * as React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  Stack,
  Divider,
  Button,
  TextField,
  MenuItem,
  Alert,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export const BLOG_CATEGORIES = [
  { id: "cleaning", label: "Cleaning Services" },
  { id: "hvac", label: "HVAC Services" },
  { id: "water-mitigation", label: "Water Mitigation Services" },
  { id: "roofing", label: "Roofing Services" },
];

// Helper for consistent dummy images (replace with your own later)
const img = (seed) => `https://picsum.photos/seed/${seed}/900/520`;

/**
 * Blog content blocks:
 * - { type: "h2" | "h3" | "p" | "ul" | "quote" | "callout" | "divider" }
 * - For "ul": pass items: string[]
 * - For "callout": pass severity: "info"|"warning"|"success"|"error", title?: string, text: string
 */
export const BLOGS = [
  // ------------------------------------------------------------
  // CLEANING SERVICES (Bloomington, IN)
  // ------------------------------------------------------------
  {
    id: "clean-chores-to-checklist",
    categoryId: "cleaning",
    categoryLabel: "Cleaning Services",
    title: "From Chores to Checklist: A Practical Whole‑Home Cleaning Plan (Bloomington, IN)",
    author: "Home Pros Team",
    date: "2025-10-02",
    imageUrl: img("homepros-clean-1"),
    excerpt:
      "A room-by-room checklist that keeps your home tidy without losing your weekends—plus a simple schedule that actually sticks.",
    readTime: "6 min",
    tags: ["checklist", "routine", "whole-home"],
    content: [
      { type: "p", text: "A clean home shouldn’t feel like a second job. The trick is to split tasks into bite‑size pieces and repeat them on a predictable schedule. Below is a simple plan we use with Home Pros clients around Bloomington, Indiana." },
      { type: "callout", severity: "info", title: "Quick idea", text: "Start with a 15‑minute nightly reset. Consistency beats marathon cleaning sessions." },
      { type: "h2", text: "The 4‑part weekly rhythm" },
      { type: "ul", items: [
        "Daily: dishes, counters, quick floor sweep in high‑traffic spots",
        "Weekly: bathrooms + dusting + vacuum/mop",
        "Monthly: baseboards, light fixtures, inside microwave, spot‑clean walls",
        "Seasonal: deep clean appliances, windows, closets, and storage areas"
      ]},
      { type: "h2", text: "Room‑by‑room essentials" },
      { type: "h3", text: "Kitchen" },
      { type: "ul", items: [
        "Wipe counters and high‑touch handles (fridge, cabinets)",
        "Sanitize sink + faucet",
        "Clean stovetop after cooking to avoid baked‑on buildup",
        "Empty trash and replace liner"
      ]},
      { type: "h3", text: "Bathrooms" },
      { type: "ul", items: [
        "Spray shower walls after use to reduce soap scum",
        "Disinfect toilet (seat + base)",
        "Wipe mirrors and faucets",
        "Swap towels—fresh towels make the room feel instantly cleaner"
      ]},
      { type: "h3", text: "Living areas & bedrooms" },
      { type: "ul", items: [
        "Declutter surfaces first (it’s the fastest visual upgrade)",
        "Dust top‑down: shelves → tables → baseboards",
        "Vacuum edges and under furniture on a rotating schedule"
      ]},
      { type: "quote", text: "If you can’t do everything, do the “visible five”: counters, sink, toilet, floors, and clutter." },
      { type: "h2", text: "When it’s time to call a pro" },
      { type: "p", text: "If allergies are flaring, you’re moving in/out, hosting guests, or life is just busy, a professional cleaning reset can make your routine feel manageable again. Home Pros can help with one‑time deep cleans or recurring service in Bloomington and nearby areas." },
    ],
  },
  {
    id: "clean-move-in-move-out",
    categoryId: "cleaning",
    categoryLabel: "Cleaning Services",
    title: "Move‑In / Move‑Out Cleaning: What Bloomington Homeowners Forget (and Regret)",
    author: "Alyssa Grant",
    date: "2025-09-15",
    imageUrl: img("homepros-clean-2"),
    excerpt:
      "Security deposit saver: the overlooked spots that property managers notice first—plus a checklist you can hand to any cleaner.",
    readTime: "7 min",
    tags: ["move-out", "rental", "deep-clean"],
    content: [
      { type: "p", text: "Moving is chaotic. Cleaning is usually the last thing anyone wants to do—until a final walk‑through reveals the “small stuff” that costs real money." },
      { type: "h2", text: "The most missed areas (and why they matter)" },
      { type: "ul", items: [
        "Inside oven + stove knobs (grease builds up where you don’t see it daily)",
        "Fridge gaskets and drawers (crumbs + spills hide here)",
        "Window tracks and sills (dust is obvious in sunlight)",
        "Baseboards and door frames (scuffs stand out in empty rooms)",
        "Ceiling fan blades (the first spin throws dust everywhere)"
      ]},
      { type: "callout", severity: "warning", title: "Don’t skip this", text: "Take photos after cleaning. It’s your best backup if there’s a deposit dispute." },
      { type: "h2", text: "A simple 2‑hour sprint plan" },
      { type: "ul", items: [
        "00:00–00:20: Empty trash, remove items, open windows",
        "00:20–01:10: Kitchen deep clean (oven, fridge, counters, sink)",
        "01:10–01:40: Bathrooms (toilet, shower/tub, mirrors)",
        "01:40–02:00: Floors + baseboards + quick final wipe-down"
      ]},
      { type: "h2", text: "Pro tip for Bloomington rentals" },
      { type: "p", text: "If you’re on a tight timeline, schedule a move‑out clean the day after movers (or the same day, after loading). Empty rooms clean faster and more thoroughly." },
      { type: "p", text: "Home Pros offers move‑in and move‑out cleaning in Bloomington, IN—so you can focus on the move, not the grime." },
    ],
  },
  {
    id: "clean-allergy-friendly",
    categoryId: "cleaning",
    categoryLabel: "Cleaning Services",
    title: "Allergy‑Friendly Cleaning in Indiana: Small Changes That Make a Big Difference",
    author: "Jordan Lee",
    date: "2025-08-20",
    imageUrl: img("homepros-clean-3"),
    excerpt:
      "A realistic routine to reduce dust, dander, and pollen indoors—without harsh chemicals or complicated gadgets.",
    readTime: "5 min",
    tags: ["allergies", "indiana", "indoor-air"],
    content: [
      { type: "p", text: "Indiana allergy season can be rough. The good news: you don’t need a perfect home—just a few targeted habits that reduce the biggest triggers." },
      { type: "h2", text: "Focus on the top 3 indoor irritants" },
      { type: "ul", items: [
        "Dust (especially on fabrics and soft surfaces)",
        "Pet dander (sticks to upholstery and rugs)",
        "Pollen tracked indoors (entryways and shoes)"
      ]},
      { type: "h2", text: "High‑impact adjustments" },
      { type: "ul", items: [
        "Vacuum with a HEPA filter 2–3x/week in common areas",
        "Wash bedding weekly in hot water (check fabric care tags)",
        "Use a damp microfiber cloth to dust (dry dusting just moves it around)",
        "Add a shoe‑off rule near the entry",
        "Change HVAC filters on schedule (yes, it’s part of cleaning)"
      ]},
      { type: "callout", severity: "success", title: "Easy win", text: "Put a small basket by the door for ‘outside shoes’ so the habit sticks." },
      { type: "h2", text: "When professional cleaning helps most" },
      { type: "p", text: "Deep carpet extraction, upholstery cleaning, and thorough dust removal can noticeably reduce symptoms—especially after winter when windows stay closed for months." },
    ],
  },

  // ------------------------------------------------------------
  // HVAC SERVICES
  // ------------------------------------------------------------
  {
    id: "hvac-fall-tune-up",
    categoryId: "hvac",
    categoryLabel: "HVAC Services",
    title: "Fall HVAC Tune‑Up Checklist for Bloomington Homes (Before the First Cold Snap)",
    author: "Chris Patel",
    date: "2025-10-10",
    imageUrl: img("homepros-hvac-1"),
    excerpt:
      "A homeowner-friendly checklist for better comfort, lower energy bills, and fewer surprise breakdowns this winter.",
    readTime: "6 min",
    tags: ["tune-up", "winter", "maintenance"],
    content: [
      { type: "p", text: "When temperatures drop in Bloomington, your furnace becomes the MVP. A quick tune‑up routine can prevent ‘no heat’ calls on the coldest week of the year." },
      { type: "h2", text: "Your pre‑winter checklist" },
      { type: "ul", items: [
        "Replace or clean the air filter (most homes: every 1–3 months)",
        "Check thermostat batteries and settings",
        "Open supply vents and ensure return vents aren’t blocked",
        "Listen for new rattles, squeals, or clicking during startup",
        "Check the area around the furnace for safe clearance (no storage piles)"
      ]},
      { type: "callout", severity: "warning", title: "Safety note", text: "If you smell gas or see soot around the unit, shut it off and call a licensed HVAC tech immediately." },
      { type: "h2", text: "What a professional tune‑up usually includes" },
      { type: "ul", items: [
        "Inspecting heat exchanger and burners",
        "Checking ignition and flame sensor",
        "Measuring airflow and temperature rise",
        "Verifying safety controls and carbon monoxide risk factors",
        "System performance recommendations"
      ]},
      { type: "p", text: "Home Pros can connect you with trusted HVAC service for tune‑ups, repairs, and seasonal maintenance in Bloomington, Indiana." },
    ],
  },
  {
    id: "hvac-uneven-rooms",
    categoryId: "hvac",
    categoryLabel: "HVAC Services",
    title: "Why One Room Is Always Hot or Cold (and What to Do About It)",
    author: "Home Pros Team",
    date: "2025-07-29",
    imageUrl: img("homepros-hvac-2"),
    excerpt:
      "Uneven temperatures are common—but they’re not ‘normal.’ Here are the most likely causes and the best fixes.",
    readTime: "7 min",
    tags: ["comfort", "ducts", "airflow"],
    content: [
      { type: "p", text: "If your bedroom is freezing while the living room is perfect, you’re not alone. Uneven heating/cooling usually comes down to airflow, insulation, or system sizing." },
      { type: "h2", text: "Common causes" },
      { type: "ul", items: [
        "Closed/blocked vents or furniture covering returns",
        "Leaky or disconnected ductwork (especially in attics/crawl spaces)",
        "Poor insulation in exterior walls or attic above the room",
        "Solar heat gain (big windows facing west)",
        "Aging HVAC system struggling to move enough air"
      ]},
      { type: "h2", text: "DIY checks first" },
      { type: "ul", items: [
        "Make sure all vents are open and unobstructed",
        "Replace the air filter if it’s dirty",
        "Check weatherstripping around doors/windows",
        "Try a ceiling fan: clockwise in winter, counterclockwise in summer"
      ]},
      { type: "callout", severity: "info", title: "If the problem is just one room", text: "That’s a clue: it often points to a duct, insulation, or window issue localized to that space." },
      { type: "h2", text: "When to bring in a pro" },
      { type: "p", text: "If DIY steps don’t help, ask for an airflow/duct evaluation. A small duct repair or balancing adjustment can make a huge difference." },
    ],
  },
  {
    id: "hvac-filter-guide",
    categoryId: "hvac",
    categoryLabel: "HVAC Services",
    title: "HVAC Air Filters: MERV Ratings Explained (Without the Confusion)",
    author: "Rina Kapoor",
    date: "2025-06-12",
    imageUrl: img("homepros-hvac-3"),
    excerpt:
      "Choosing the right filter improves indoor air quality—without choking your system. Here’s a simple way to pick.",
    readTime: "5 min",
    tags: ["filters", "merv", "air-quality"],
    content: [
      { type: "p", text: "Air filters are one of the cheapest ways to protect your HVAC system and your lungs—but ‘better’ isn’t always ‘higher MERV.’" },
      { type: "h2", text: "The quick guide" },
      { type: "ul", items: [
        "MERV 6–8: Basic dust/lint control; good for many homes",
        "MERV 9–11: Better for pet dander and allergy concerns",
        "MERV 12–13: Higher filtration; may require HVAC compatibility to avoid airflow issues"
      ]},
      { type: "callout", severity: "warning", title: "Watch airflow", text: "If your system isn’t designed for high resistance filters, it can reduce airflow and efficiency." },
      { type: "h2", text: "How often to change it?" },
      { type: "ul", items: [
        "1-inch filters: typically every 30–90 days",
        "Thicker media filters: often every 6–12 months (check manufacturer guidance)",
        "More often if you have pets, renovations, or high dust"
      ]},
      { type: "p", text: "If you’re unsure which filter is best for your system, an HVAC pro can confirm the right range for comfort and efficiency." },
    ],
  },

  // ------------------------------------------------------------
  // WATER MITIGATION SERVICES
  // ------------------------------------------------------------
  {
    id: "water-mitigation-first-24-hours",
    categoryId: "water-mitigation",
    categoryLabel: "Water Mitigation Services",
    title: "The First 24 Hours After Water Damage: What to Do (and What to Avoid)",
    author: "Maya Thompson",
    date: "2025-11-01",
    imageUrl: img("homepros-water-1"),
    excerpt:
      "Fast action matters. Here’s a practical, step-by-step plan to reduce damage and protect your home.",
    readTime: "8 min",
    tags: ["emergency", "flooding", "dry-out"],
    content: [
      { type: "p", text: "Water damage can spread quickly—into flooring, drywall, and even electrical systems. The first day is the window where you can save thousands in repairs." },
      { type: "callout", severity: "warning", title: "Safety first", text: "If water is near outlets, electrical panels, or appliances, shut off power at the breaker (only if it’s safe to do so) and call a professional." },
      { type: "h2", text: "Step-by-step: the first day" },
      { type: "ul", items: [
        "Stop the source (shut off valve, patch leak, or call a plumber)",
        "Document everything (photos and short videos for insurance)",
        "Remove standing water (wet vac / pump if safe)",
        "Start drying: fans + dehumidifier + open doors for airflow",
        "Pull up wet rugs and move furniture off wet floors",
        "Call a mitigation pro if water touched drywall, insulation, or subfloor"
      ]},
      { type: "h2", text: "What NOT to do" },
      { type: "ul", items: [
        "Don’t ignore ‘small’ wet spots—moisture can travel behind walls",
        "Don’t paint over stains before drying (it traps moisture)",
        "Don’t run HVAC if you suspect contamination or mold risk",
        "Don’t assume it’s dry because the surface feels dry"
      ]},
      { type: "quote", text: "Drying isn’t just ‘airing out’—it’s controlled moisture removal." },
      { type: "p", text: "Home Pros can help coordinate water mitigation services in Bloomington, IN, including inspection, drying, and restoration planning." },
    ],
  },
  {
    id: "water-mitigation-mold-prevention",
    categoryId: "water-mitigation",
    categoryLabel: "Water Mitigation Services",
    title: "Mold Prevention After a Leak: The Simple Rules Most People Miss",
    author: "Home Pros Team",
    date: "2025-09-06",
    imageUrl: img("homepros-water-2"),
    excerpt:
      "Mold can start fast. These practical rules help you prevent it after leaks, basement seepage, or a small flood.",
    readTime: "6 min",
    tags: ["mold", "leaks", "basement"],
    content: [
      { type: "p", text: "Mold needs moisture and time. The best prevention strategy is speed: dry it properly, and remove what can’t be dried." },
      { type: "h2", text: "The 3 rules" },
      { type: "ul", items: [
        "Dry within 24–48 hours (aim for faster)",
        "Remove wet porous materials if they can’t be fully dried (carpet pad, insulation)",
        "Keep humidity low (dehumidifier + closed windows if it’s humid outside)"
      ]},
      { type: "callout", severity: "info", title: "Hidden moisture is the real problem", text: "Water behind baseboards or in wall cavities can grow mold even when floors feel dry." },
      { type: "h2", text: "Signs you need a professional assessment" },
      { type: "ul", items: [
        "Musty odor after drying attempts",
        "Bubbling paint or soft drywall",
        "Water sat longer than a day",
        "Repeated moisture in the same area"
      ]},
      { type: "p", text: "If you’re in Bloomington and want peace of mind, a moisture inspection can confirm whether drying was successful." },
    ],
  },
  {
    id: "water-mitigation-insurance-basics",
    categoryId: "water-mitigation",
    categoryLabel: "Water Mitigation Services",
    title: "Water Damage & Insurance: What to Document (So Claims Go Smoother)",
    author: "Sam Rivera",
    date: "2025-05-30",
    imageUrl: img("homepros-water-3"),
    excerpt:
      "A practical documentation checklist for homeowners—photos, notes, receipts, and the details adjusters typically ask for.",
    readTime: "7 min",
    tags: ["insurance", "documentation", "claims"],
    content: [
      { type: "p", text: "Every claim is different, but strong documentation helps everyone move faster—especially when damage isn’t obvious from one photo." },
      { type: "h2", text: "Document these items right away" },
      { type: "ul", items: [
        "The source of water (pipe, appliance, storm, roof leak)",
        "Where water traveled (rooms, floors, walls)",
        "Close-ups + wide shots of damage",
        "Serial numbers for affected appliances (if applicable)",
        "Any emergency actions taken (shutoff, extraction, fans)"
      ]},
      { type: "h2", text: "Keep a simple incident log" },
      { type: "ul", items: [
        "Date/time you noticed the issue",
        "When you stopped the source",
        "Who you called and when",
        "What remediation steps were performed",
        "Receipts (equipment rental, plumber, mitigation)"
      ]},
      { type: "callout", severity: "success", title: "Pro tip", text: "Email the photos to yourself (or a shared folder). It creates a timestamped backup." },
      { type: "p", text: "Home Pros can help you line up water mitigation services and keep the process organized from day one." },
    ],
  },

  // ------------------------------------------------------------
  // ROOFING SERVICES
  // ------------------------------------------------------------
  {
    id: "roofing-storm-damage-signs",
    categoryId: "roofing",
    categoryLabel: "Roofing Services",
    title: "Storm Damage Roof Check: What to Look for After Wind or Hail in Bloomington",
    author: "Ethan Brooks",
    date: "2025-11-20",
    imageUrl: img("homepros-roof-1"),
    excerpt:
      "A safe, ground-level checklist to spot common roof damage after Indiana storms—plus when to call a roofer.",
    readTime: "6 min",
    tags: ["storm", "hail", "inspection"],
    content: [
      { type: "p", text: "After a big storm, the biggest mistake is waiting until a ceiling stain appears. Many roof issues show up outside first—and early detection can prevent interior damage." },
      { type: "callout", severity: "warning", title: "Safety note", text: "Avoid climbing onto the roof. Most damage can be spotted from the ground or with photos taken from a safe vantage point." },
      { type: "h2", text: "Ground-level signs of roof trouble" },
      { type: "ul", items: [
        "Missing or lifted shingles (especially along edges and ridgelines)",
        "Shingle granules collecting in gutters/downspouts",
        "Dented gutters, downspouts, or roof vents (hail clue)",
        "Flashing that looks bent, loose, or displaced",
        "Branches or debris resting on the roof"
      ]},
      { type: "h2", text: "Inside-the-home clues" },
      { type: "ul", items: [
        "New water stains on ceilings or around vents",
        "Musty odor in attic areas",
        "Drips during heavy rain—even if they stop later"
      ]},
      { type: "p", text: "If you suspect damage, a professional roof inspection can confirm what needs repair and help you plan next steps." },
    ],
  },
  {
    id: "roofing-roof-life-extension",
    categoryId: "roofing",
    categoryLabel: "Roofing Services",
    title: "How to Extend the Life of Your Roof: Simple Maintenance That Pays Off",
    author: "Home Pros Team",
    date: "2025-08-05",
    imageUrl: img("homepros-roof-2"),
    excerpt:
      "A few routine checks can add years to your roof. Here’s what to do seasonally—and what to avoid.",
    readTime: "7 min",
    tags: ["maintenance", "gutters", "prevention"],
    content: [
      { type: "p", text: "Roof replacements are expensive. Most homeowners can add real lifespan to a roof by preventing the common causes of premature wear: moisture, debris, and ventilation problems." },
      { type: "h2", text: "Seasonal roof care" },
      { type: "ul", items: [
        "Spring: Clear debris, check for loose shingles after storms",
        "Summer: Inspect attic ventilation and signs of heat buildup",
        "Fall: Clean gutters and downspouts to prevent ice dams and overflow",
        "Winter: Watch for heavy snow buildup and icicles near eaves"
      ]},
      { type: "callout", severity: "info", title: "Gutters matter", text: "Clogged gutters push water under shingles and rot fascia boards—maintenance here protects your roof and siding." },
      { type: "h2", text: "What to avoid" },
      { type: "ul", items: [
        "Pressure washing shingles (can strip protective granules)",
        "Ignoring small flashing gaps around chimneys and vents",
        "Letting branches scrape the roof during wind",
        "Blocking soffit vents with insulation"
      ]},
      { type: "p", text: "If you want a quick checkup, Home Pros can connect you with roofing pros for inspections and maintenance in Bloomington." },
    ],
  },
  {
    id: "roofing-roof-leak-troubleshooting",
    categoryId: "roofing",
    categoryLabel: "Roofing Services",
    title: "Roof Leak Troubleshooting: Why the Drip Isn’t Always Above the Stain",
    author: "Nina Carter",
    date: "2025-06-28",
    imageUrl: img("homepros-roof-3"),
    excerpt:
      "Leaks travel. Here’s how roof water moves and what homeowners can check safely before a roofer arrives.",
    readTime: "8 min",
    tags: ["leaks", "attic", "flashing"],
    content: [
      { type: "p", text: "A ceiling stain feels like the obvious clue—but roof leaks often enter at one point and show up somewhere else. Water follows framing and insulation before it finally becomes visible." },
      { type: "h2", text: "Common entry points" },
      { type: "ul", items: [
        "Flashing around chimneys, skylights, and plumbing vents",
        "Valleys where roof planes meet",
        "Nail pops or failed sealant on exposed fasteners",
        "Damaged shingles near edges and ridges"
      ]},
      { type: "h2", text: "Safe checks you can do" },
      { type: "ul", items: [
        "Look in the attic with a flashlight during (or right after) rain",
        "Check for darkened wood, wet insulation, or moldy smell",
        "Photograph the stain and mark its edges (to track growth)",
        "Inspect gutters/downspouts for overflow patterns"
      ]},
      { type: "callout", severity: "warning", title: "Avoid DIY patch kits as a long-term fix", text: "Temporary sealants can hide damage and complicate proper repairs later." },
      { type: "p", text: "If you’re seeing stains or drips in Bloomington, a professional roof inspection can pinpoint the entry point and recommend the right repair." },
    ],
  },
];

// ------------------------- helpers -------------------------
const formatDate = (iso) => {
  // Display a friendly date while keeping ISO in data
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return iso;
  }
};

const getCategoryLabel = (categoryId) =>
  BLOG_CATEGORIES.find((c) => c.id === categoryId)?.label ?? "All Services";

const matchesSearch = (blog, q) => {
  if (!q) return true;
  const needle = q.toLowerCase().trim();
  const hay = [
    blog.title,
    blog.author,
    blog.categoryLabel,
    blog.excerpt,
    ...(blog.tags || []),
    ...(blog.content || []).map((b) => (b.text ? b.text : "")).join(" "),
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
};

// ------------------------- BlogDetails -------------------------
export function BlogDetails({ blog, onBack }) {
  if (!blog) return null;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2 }}
        variant="text"
      >
        Back to all blogs
      </Button>

      <Typography variant="overline" sx={{ letterSpacing: 0.8 }}>
        {blog.categoryLabel} • {formatDate(blog.date)} • {blog.readTime}
      </Typography>

      <Typography variant="h3" sx={{ mt: 1, mb: 1.5, fontWeight: 700 }}>
        {blog.title}
      </Typography>

      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        By <Box component="span" sx={{ fontWeight: 600 }}>{blog.author}</Box>
      </Typography>

      <Box
        component="img"
        src={blog.imageUrl}
        alt={blog.title}
        sx={{
          width: "100%",
          height: "auto",
          borderRadius: 3,
          mb: 3,
          boxShadow: 1,
        }}
      />

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <BlogContent blocks={blog.content} />
      </Paper>

      {(blog.tags?.length ?? 0) > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: "wrap" }}>
          {blog.tags.map((t) => (
            <Chip key={t} label={t} size="small" />
          ))}
        </Stack>
      )}
    </Container>
  );
}

// ------------------------- BlogContent -------------------------
export function BlogContent({ blocks = [] }) {
  return (
    <Box>
      {blocks.map((b, idx) => {
        if (b.type === "divider") {
          return <Divider key={idx} sx={{ my: 2 }} />;
        }

        if (b.type === "h2") {
          return (
            <Typography key={idx} variant="h5" sx={{ mt: 2.5, mb: 1, fontWeight: 700 }}>
              {b.text}
            </Typography>
          );
        }

        if (b.type === "h3") {
          return (
            <Typography key={idx} variant="h6" sx={{ mt: 2, mb: 0.75, fontWeight: 700 }}>
              {b.text}
            </Typography>
          );
        }

        if (b.type === "p") {
          return (
            <Typography key={idx} variant="body1" sx={{ mb: 1.5, lineHeight: 1.75 }}>
              {b.text}
            </Typography>
          );
        }

        if (b.type === "ul") {
          return (
            <Box key={idx} component="ul" sx={{ pl: 3, mb: 2, mt: 0.5 }}>
              {b.items?.map((item, j) => (
                <Box key={j} component="li" sx={{ mb: 0.75 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          );
        }

        if (b.type === "quote") {
          return (
            <Box
              key={idx}
              sx={{
                borderLeft: "4px solid",
                borderColor: "divider",
                pl: 2,
                py: 0.5,
                my: 2,
              }}
            >
              <Typography variant="body1" sx={{ fontStyle: "italic", lineHeight: 1.75 }}>
                “{b.text}”
              </Typography>
            </Box>
          );
        }

        if (b.type === "callout") {
          return (
            <Alert
              key={idx}
              severity={b.severity || "info"}
              sx={{ my: 2, borderRadius: 2 }}
            >
              {b.title ? (
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                  {b.title}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                {b.text}
              </Typography>
            </Alert>
          );
        }

        // Fallback
        return (
          <Typography key={idx} variant="body1" sx={{ mb: 1.5, lineHeight: 1.75 }}>
            {b.text ?? ""}
          </Typography>
        );
      })}
    </Box>
  );
}

// ------------------------- AllBlogs (default export) -------------------------
export default function AllBlogs() {
  const [category, setCategory] = React.useState("all");
  const [search, setSearch] = React.useState("");
  const [selectedBlogId, setSelectedBlogId] = React.useState(null);

  const selectedBlog = React.useMemo(
    () => BLOGS.find((b) => b.id === selectedBlogId) || null,
    [selectedBlogId]
  );

  const filtered = React.useMemo(() => {
    const byCategory =
      category === "all" ? BLOGS : BLOGS.filter((b) => b.categoryId === category);
    return byCategory.filter((b) => matchesSearch(b, search));
  }, [category, search]);

  if (selectedBlog) {
    return <BlogDetails blog={selectedBlog} onBack={() => setSelectedBlogId(null)} />;
  }

  return (
    <Box sx={{ py: 5 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.75 }}>
          Home Pros Blog
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, maxWidth: 820 }}>
          Tips and guides for homeowners in Bloomington, Indiana—organized by service category.
        </Typography>

        {/* Filters */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Search blogs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Try: leaks, checklist, tune-up..."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Filter by service"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <MenuItem value="all">All Services</MenuItem>
                {BLOG_CATEGORIES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                <Chip
                  label={`${filtered.length} result${filtered.length === 1 ? "" : "s"}`}
                  variant="outlined"
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setCategory("all");
                    setSearch("");
                  }}
                >
                  Reset
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Category chips (optional quick filter) */}
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
          <Chip
            label="All"
            clickable
            color={category === "all" ? "primary" : "default"}
            onClick={() => setCategory("all")}
            sx={{ mb: 1 }}
          />
          {BLOG_CATEGORIES.map((c) => (
            <Chip
              key={c.id}
              label={c.label}
              clickable
              color={category === c.id ? "primary" : "default"}
              onClick={() => setCategory(c.id)}
              sx={{ mb: 1 }}
            />
          ))}
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Blog cards */}
        <Grid container spacing={3}>
          {filtered.map((blog) => (
            <Grid key={blog.id} item xs={12} sm={6} md={4}>
              <Card sx={{ height: "100%", borderRadius: 3 }} variant="outlined">
                <CardActionArea
                  onClick={() => setSelectedBlogId(blog.id)}
                  sx={{ height: "100%", alignItems: "stretch" }}
                >
                  <CardMedia
                    component="img"
                    height="170"
                    image={blog.imageUrl}
                    alt={blog.title}
                  />
                  <CardContent>
                    <Typography variant="overline" sx={{ letterSpacing: 0.6 }}>
                      {getCategoryLabel(blog.categoryId)} • {formatDate(blog.date)}
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{ mt: 0.5, fontWeight: 800, lineHeight: 1.25 }}
                    >
                      {blog.title}
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 1.25 }}>
                      {blog.excerpt}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
                      <Chip label={blog.readTime} size="small" variant="outlined" />
                      <Chip label={`By ${blog.author}`} size="small" variant="outlined" />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}

          {filtered.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                No blogs matched your search. Try a different keyword or reset filters.
              </Alert>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}
